/**
 * Branding provider for white-label support.
 *
 * Fetches agency branding from /api/v1/config/branding/ on mount and provides
 * it to the entire React tree. The context object, types, defaults and hooks
 * (useBranding / useBrandingContext) live in ./branding so this module exports
 * ONLY the provider component (keeps Fast Refresh + lint happy).
 *
 * - Sets the agency name / document title (NOT colours: per #758 the dashboard
 *   chrome is fixed product design — agency branding colours apply to exported
 *   documents only, never the live dashboard CSS variables)
 * - Sets document.title dynamically
 * - Swaps index.html's platform icons (the Beyond mark) for the agency's
 *   favicon, and the iOS apple-touch-icon for its compact logo / favicon —
 *   the latter off portal routes, which own that link (D9-WLB-004, #3730)
 * - Falls back to neutral generic defaults (agency_name "CRM") if the
 *   endpoint is unreachable, so a fetch failure never reveals another agency
 */
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { apiRequest } from "../api/client";
import { BrandingContext, DEFAULT_BRANDING } from "./branding";
import type { Branding } from "./branding";
import { useInRouterContext, useLocation } from "react-router-dom";
import { agencyCrmLabel, agencyTouchIconUrl } from "../utils/branding";
import { isPortalPath } from "../utils/portalRoutes";

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    apiRequest<Branding>("/config/branding/", { method: "GET" }, controller.signal)
      .then((data) => {
        setBranding(data);
      })
      .catch(() => {
        // Endpoint unreachable or errored (or request aborted) — keep defaults.
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, []);

  // Set dynamic page title. The client-facing portal surfaces set their own
  // ("{agency} — Your purchase", D9-WLB-003) and must never show "CRM" to a
  // client, so this write is skipped there.
  useEffect(() => {
    if (isPortalPath(window.location.pathname)) return;
    document.title = agencyCrmLabel(branding.agency_name);
  }, [branding.agency_name]);

  // An agency's own favicon replaces the platform default (the Beyond mark
  // in index.html). index.html ships two tab-icon links -- an SVG and a PNG
  // fallback -- and a browser may pick either, so both are repointed; a stale
  // `type`/`sizes` would misdescribe the agency's file, so those go too. The
  // portal never touches `rel="icon"`, so this applies on every route.
  useEffect(() => {
    const faviconUrl = branding.favicon_url;
    if (!faviconUrl) return;
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]'),
    );
    if (links.length === 0) {
      const link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
      links.push(link);
    }
    for (const link of links) {
      link.removeAttribute("type");
      link.removeAttribute("sizes");
      link.href = faviconUrl;
    }
  }, [branding.favicon_url]);

  const touchIconUrl = agencyTouchIconUrl(branding);
  const inRouter = useInRouterContext();

  const value = useMemo(() => ({ branding, loading }), [branding, loading]);

  return (
    <BrandingContext.Provider value={value}>
      {inRouter ? (
        <RoutedTouchIconSync url={touchIconUrl} />
      ) : (
        <TouchIconSync url={touchIconUrl} pathname={window.location.pathname} />
      )}
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * The CRM's iOS home-screen icon (#3730): the agency's compact logo, else its
 * favicon (`agencyTouchIconUrl`), else index.html's platform default stands.
 *
 * On portal routes `usePortalDocumentChrome` (D9-WLB-004) owns this link --
 * it claims it and restores the value it found on leaving -- so this write is
 * skipped there, exactly as the title write is. It is keyed on the pathname
 * so that, after the portal's undo has run on the way out (unmount cleanups
 * precede the new route's effects), the CRM value is put back: the portal may
 * have captured the platform default on a direct portal load.
 */
function TouchIconSync({ url, pathname }: { url: string | null; pathname: string }) {
  useEffect(() => {
    if (!url || isPortalPath(pathname)) return;
    let link = document.head.querySelector<HTMLLinkElement>(
      'link[rel="apple-touch-icon"]',
    );
    if (!link) {
      link = document.createElement("link");
      link.rel = "apple-touch-icon";
      document.head.appendChild(link);
    }
    // index.html's `sizes="180x180"` describes the platform PNG, not this file.
    link.removeAttribute("sizes");
    link.removeAttribute("type");
    link.href = url;
  }, [url, pathname]);
  return null;
}

/** {@link TouchIconSync} following the router's location (the app's case). */
function RoutedTouchIconSync({ url }: { url: string | null }) {
  const { pathname } = useLocation();
  return <TouchIconSync url={url} pathname={pathname} />;
}
